// So 2 version theo SemVer 2.0.0 §11 (có prerelease) — dùng chung cho pack
// (`compare-pack-semver-and-detect-downgrade.ts`, ticket #191) và cổng update CLI
// (`check-cli-version-gate.ts`, F-18).
//
// Tách khỏi module pack để cổng CLI không phải import một API mang tên "pack".
// Chỉ dùng lại `parseSemVerTag` cho khâu PARSE (regex đã có test riêng); phần so
// sánh tự viết đủ §11 vì `pickLatestStableSemVerTag` bỏ qua prerelease.
//
// Không thêm dependency (`semver` npm) — bất biến của ticket #191.

import { parseSemVerTag } from "./pick-latest-stable-semver-tag.js";

// `ParsedSemVer` không được export ở module gốc ⇒ suy ra kiểu từ chính hàm parse.
export type ParsedSemVerWithPrerelease = NonNullable<ReturnType<typeof parseSemVerTag>>;

// So 2 chuỗi prerelease theo SemVer 2.0.0 §11:
//   - stable (null) > prerelease
//   - identifier toàn số so bằng SỐ (beta.10 > beta.2, KHÔNG lexical)
//   - identifier số < identifier chữ
//   - còn lại so lexical ASCII
//   - tập ÍT field < tập NHIỀU field khi phần đầu bằng nhau (beta < beta.0)
function comparePrereleaseIdentifiers(a: string | null, b: string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;

  const left = a.split(".");
  const right = b.split(".");
  const length = Math.max(left.length, right.length);

  for (let i = 0; i < length; i++) {
    const x = left[i];
    const y = right[i];
    if (x === undefined) return -1;
    if (y === undefined) return 1;

    const xIsNumeric = /^\d+$/.test(x);
    const yIsNumeric = /^\d+$/.test(y);
    if (xIsNumeric && yIsNumeric) {
      const diff = Number(x) - Number(y);
      if (diff !== 0) return diff < 0 ? -1 : 1;
      continue;
    }
    if (xIsNumeric !== yIsNumeric) return xIsNumeric ? -1 : 1;
    if (x !== y) return x < y ? -1 : 1;
  }

  return 0;
}

/** So đầy đủ 2 version đã parse. Trả -1 / 0 / 1. */
export function compareParsedSemVer(
  a: ParsedSemVerWithPrerelease,
  b: ParsedSemVerWithPrerelease,
): number {
  if (a.major !== b.major) return a.major < b.major ? -1 : 1;
  if (a.minor !== b.minor) return a.minor < b.minor ? -1 : 1;
  if (a.patch !== b.patch) return a.patch < b.patch ? -1 : 1;
  return comparePrereleaseIdentifiers(a.prerelease, b.prerelease);
}

/**
 * Parse fail-soft: mọi input không phải chuỗi SemVer đều thành null.
 * Trim vì file version có thể còn `\n` cuối, mà regex gốc neo `^…$`.
 */
export function parseSemVerLoose(
  value: string | null | undefined,
): ParsedSemVerWithPrerelease | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return parseSemVerTag(trimmed);
}
